// ============================================================================
// KANBAN STYLE PRESETS
// Jira-inspired customizable board styling with 10 distinct visual presets
// Clients can select their preferred design or customize further
// Date: January 2026
// ============================================================================

// ============================================================================
// TYPES
// ============================================================================

export interface KanbanStylePreset {
  id: string;
  name: string;
  description: string;
  thumbnail?: string; // Preview image URL for settings UI
  
  // Board-level styling
  board: {
    background: string; // Board background color/gradient
    columnGap: string; // Gap between columns
    padding: string; // Board padding
    borderRadius: string; // Overall board rounding
  };
  
  // Column styling
  column: {
    background: string; // Column background
    headerBackground: string; // Column header bg
    borderRadius: string;
    border: string;
    shadow: string;
    headerTextColor: string;
    subtitleTextColor: string; // Column header subtitle/description text
    countBadge: {
      background: string;
      textColor: string;
      borderRadius: string;
    };
    // Column-specific color intensity multipliers
    colorIntensity: 'subtle' | 'medium' | 'vibrant';
  };
  
  // Card styling  
  card: {
    background: string;
    backgroundHover: string;
    border: string;
    borderRadius: string;
    shadow: string;
    shadowHover: string;
    padding: string;
    // Priority left border styling
    priorityBorderWidth: string;
    // Title styling
    titleSize: string;
    titleWeight: string;
    titleColor: string; // Card title text color (ensures contrast on dark presets)
    // Metadata styling
    metaSize: string;
    metaColor: string;
    // Item number badge styling
    itemNumberBg: string;
    itemNumberColor: string;
    // Tag styling
    tagStyle: 'pill' | 'square' | 'outline' | 'minimal';
  };
  
  // Drag & drop styling
  dragDrop: {
    dragCardScale: string;
    dragCardRotate: string;
    dragCardShadow: string;
    dropZoneBackground: string;
    dropZoneBorder: string;
    dropZoneScale: string;
  };
  
  // Animation preferences
  animations: {
    cardHover: boolean;
    columnExpand: boolean;
    smoothScroll: boolean;
    dragGhost: boolean;
  };
  
  // Shared view styling (FilterBar, DataGrid, Timeline, Calendar)
  // Derived from board/column/card where possible, but can override for specific views
  views: {
    // Container backgrounds
    containerBg: string;        // Main view container background
    headerBg: string;           // Toolbar/header area background
    // Borders
    containerBorder: string;    // Main view container border
    dividerBorder: string;      // Internal dividers/borders
    // Text
    headingText: string;        // Primary heading text
    bodyText: string;           // Body/cell text
    mutedText: string;          // Secondary/muted text
    // Table/grid
    rowBg: string;              // Table row background
    rowHoverBg: string;         // Row hover background
    rowBorderColor: string;     // Row divider color
    selectedRowBg: string;      // Selected row highlight
    // Inputs & controls
    inputBg: string;            // Input/search fields background
    inputBorder: string;        // Input/search fields border
    buttonBg: string;           // Secondary button backgrounds
    buttonText: string;         // Secondary button text
    // Accent (active states, pills)
    accentBg: string;           // Active/selected accent background
    accentText: string;         // Active/selected accent text
  };
}

// ============================================================================
// PRESET DEFINITIONS - 10 Jira-Inspired Styles
// ============================================================================

export const KANBAN_STYLE_PRESETS: Record<string, KanbanStylePreset> = {
  // 1. CLASSIC - Clean professional look (Default)
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Clean, professional board with subtle gradients',
    board: {
      background: 'bg-gray-50 dark:bg-gray-900',
      columnGap: 'gap-4',
      padding: 'p-4',
      borderRadius: 'rounded-lg',
    },
    column: {
      background: 'bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm',
      headerBackground: 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900',
      borderRadius: 'rounded-xl',
      border: 'border border-gray-200 dark:border-gray-700',
      shadow: 'shadow-sm',
      headerTextColor: 'text-gray-900 dark:text-white',
      subtitleTextColor: 'text-gray-500 dark:text-gray-300',
      countBadge: {
        background: 'bg-gray-100 dark:bg-gray-700',
        textColor: 'text-gray-700 dark:text-gray-300',
        borderRadius: 'rounded-full',
      },
      colorIntensity: 'medium',
    },
    card: {
      background: 'bg-white dark:bg-gray-800',
      backgroundHover: 'hover:bg-gray-50 dark:hover:bg-gray-700',
      border: 'border border-gray-200 dark:border-gray-700',
      borderRadius: 'rounded-lg',
      shadow: 'shadow-sm',
      shadowHover: 'hover:shadow-md',
      padding: 'p-3',
      priorityBorderWidth: 'border-l-[3px]',
      titleSize: 'text-sm',
      titleWeight: 'font-medium',
      titleColor: 'text-gray-900 dark:text-gray-100',
      metaSize: 'text-xs',
      metaColor: 'text-gray-500 dark:text-gray-300',
      itemNumberBg: 'bg-gray-100 dark:bg-gray-700',
      itemNumberColor: 'text-gray-500 dark:text-gray-300',
      tagStyle: 'pill',
    },
    dragDrop: {
      dragCardScale: 'scale-105',
      dragCardRotate: 'rotate-2',
      dragCardShadow: 'shadow-2xl',
      dropZoneBackground: 'bg-blue-50 dark:bg-blue-900/20',
      dropZoneBorder: 'ring-2 ring-blue-400',
      dropZoneScale: 'scale-[1.02]',
    },
    animations: {
      cardHover: true,
      columnExpand: true,
      smoothScroll: true,
      dragGhost: true,
    },
    views: {
      containerBg: 'bg-white dark:bg-gray-800',
      headerBg: 'bg-gray-50 dark:bg-gray-900',
      containerBorder: 'border-gray-200 dark:border-gray-700',
      dividerBorder: 'border-gray-200 dark:border-gray-700',
      headingText: 'text-gray-900 dark:text-white',
      bodyText: 'text-gray-700 dark:text-gray-300',
      mutedText: 'text-gray-500 dark:text-gray-300',
      rowBg: 'bg-white dark:bg-gray-900',
      rowHoverBg: 'hover:bg-gray-50 dark:hover:bg-gray-800/50',
      rowBorderColor: 'divide-gray-100 dark:divide-gray-800',
      selectedRowBg: 'bg-blue-50 dark:bg-blue-900/20',
      inputBg: 'bg-white dark:bg-gray-800',
      inputBorder: 'border-gray-200 dark:border-gray-600',
      buttonBg: 'bg-gray-100 dark:bg-gray-700',
      buttonText: 'text-gray-700 dark:text-gray-300',
      accentBg: 'bg-blue-600',
      accentText: 'text-white',
    },
  },

  // 2. VIBRANT - Bright colorful columns with strong gradients
  vibrant: {
    id: 'vibrant',
    name: 'Vibrant',
    description: 'Bold, colorful board with eye-catching gradients',
    board: {
      background: 'bg-gradient-to-br from-slate-100 via-gray-50 to-slate-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900',
      columnGap: 'gap-5',
      padding: 'p-5',
      borderRadius: 'rounded-xl',
    },
    column: {
      background: 'bg-white/90 dark:bg-gray-800/90 backdrop-blur-md',
      headerBackground: 'bg-gradient-to-r',
      borderRadius: 'rounded-2xl',
      border: 'border-2',
      shadow: 'shadow-lg shadow-black/5',
      headerTextColor: 'text-white',
      subtitleTextColor: 'text-white/70',
      countBadge: {
        background: 'bg-white/30',
        textColor: 'text-white',
        borderRadius: 'rounded-full',
      },
      colorIntensity: 'vibrant',
    },
    card: {
      background: 'bg-white dark:bg-gray-800',
      backgroundHover: 'hover:bg-gradient-to-br hover:from-white hover:to-gray-50 dark:hover:from-gray-800 dark:hover:to-gray-700',
      border: 'border-2 border-gray-100 dark:border-gray-700',
      borderRadius: 'rounded-xl',
      shadow: 'shadow-md shadow-black/5',
      shadowHover: 'hover:shadow-xl hover:shadow-black/10',
      padding: 'p-4',
      priorityBorderWidth: 'border-l-4',
      titleSize: 'text-sm',
      titleWeight: 'font-semibold',
      titleColor: 'text-gray-900 dark:text-gray-100',
      metaSize: 'text-xs',
      metaColor: 'text-gray-600 dark:text-gray-300',
      itemNumberBg: 'bg-gray-100 dark:bg-gray-700',
      itemNumberColor: 'text-gray-500 dark:text-gray-300',
      tagStyle: 'pill',
    },
    dragDrop: {
      dragCardScale: 'scale-110',
      dragCardRotate: 'rotate-3',
      dragCardShadow: 'shadow-2xl shadow-black/20',
      dropZoneBackground: 'bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30',
      dropZoneBorder: 'ring-3 ring-blue-500 ring-offset-2',
      dropZoneScale: 'scale-[1.03]',
    },
    animations: {
      cardHover: true,
      columnExpand: true,
      smoothScroll: true,
      dragGhost: true,
    },
    views: {
      containerBg: 'bg-white dark:bg-gray-800',
      headerBg: 'bg-gradient-to-r from-slate-50 to-gray-50 dark:from-gray-900 dark:to-gray-900',
      containerBorder: 'border-gray-200 dark:border-gray-700',
      dividerBorder: 'border-gray-200/60 dark:border-gray-700/60',
      headingText: 'text-gray-900 dark:text-white',
      bodyText: 'text-gray-700 dark:text-gray-300',
      mutedText: 'text-gray-500 dark:text-gray-300',
      rowBg: 'bg-white dark:bg-gray-900',
      rowHoverBg: 'hover:bg-gray-50 dark:hover:bg-gray-800/50',
      rowBorderColor: 'divide-gray-100 dark:divide-gray-800',
      selectedRowBg: 'bg-blue-50 dark:bg-blue-900/20',
      inputBg: 'bg-white dark:bg-gray-800',
      inputBorder: 'border-gray-200 dark:border-gray-600',
      buttonBg: 'bg-gray-100 dark:bg-gray-700',
      buttonText: 'text-gray-700 dark:text-gray-300',
      accentBg: 'bg-blue-600',
      accentText: 'text-white',
    },
  },

  // 3. MINIMAL - Ultra-clean, Notion-like simplicity
  minimal: {
    id: 'minimal',
    name: 'Minimal',
    description: 'Ultra-clean design inspired by Notion',
    board: {
      background: 'bg-white dark:bg-gray-900',
      columnGap: 'gap-6',
      padding: 'p-6',
      borderRadius: 'rounded-none',
    },
    column: {
      background: 'bg-transparent',
      headerBackground: 'bg-transparent',
      borderRadius: 'rounded-none',
      border: 'border-0',
      shadow: 'shadow-none',
      headerTextColor: 'text-gray-900 dark:text-white',
      subtitleTextColor: 'text-gray-500 dark:text-gray-500',
      countBadge: {
        background: 'bg-gray-200 dark:bg-gray-700',
        textColor: 'text-gray-600 dark:text-gray-400',
        borderRadius: 'rounded-md',
      },
      colorIntensity: 'subtle',
    },
    card: {
      background: 'bg-gray-50 dark:bg-gray-800',
      backgroundHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
      border: 'border-0',
      borderRadius: 'rounded-md',
      shadow: 'shadow-none',
      shadowHover: 'hover:shadow-sm',
      padding: 'p-3',
      priorityBorderWidth: 'border-l-2',
      titleSize: 'text-sm',
      titleWeight: 'font-normal',
      titleColor: 'text-gray-900 dark:text-gray-100',
      metaSize: 'text-xs',
      metaColor: 'text-gray-500 dark:text-gray-400',
      itemNumberBg: 'bg-gray-200 dark:bg-gray-700',
      itemNumberColor: 'text-gray-500 dark:text-gray-300',
      tagStyle: 'minimal',
    },
    dragDrop: {
      dragCardScale: 'scale-100',
      dragCardRotate: 'rotate-0',
      dragCardShadow: 'shadow-lg',
      dropZoneBackground: 'bg-gray-100 dark:bg-gray-800',
      dropZoneBorder: 'ring-1 ring-gray-300 dark:ring-gray-600',
      dropZoneScale: 'scale-100',
    },
    animations: {
      cardHover: false,
      columnExpand: false,
      smoothScroll: true,
      dragGhost: false,
    },
    views: {
      containerBg: 'bg-white dark:bg-gray-900',
      headerBg: 'bg-white dark:bg-gray-900',
      containerBorder: 'border-gray-200 dark:border-gray-800',
      dividerBorder: 'border-gray-100 dark:border-gray-800',
      headingText: 'text-gray-900 dark:text-white',
      bodyText: 'text-gray-700 dark:text-gray-300',
      mutedText: 'text-gray-500 dark:text-gray-400',
      rowBg: 'bg-white dark:bg-gray-900',
      rowHoverBg: 'hover:bg-gray-50 dark:hover:bg-gray-800',
      rowBorderColor: 'divide-gray-100 dark:divide-gray-800',
      selectedRowBg: 'bg-gray-100 dark:bg-gray-800',
      inputBg: 'bg-gray-50 dark:bg-gray-900',
      inputBorder: 'border-gray-200 dark:border-gray-800',
      buttonBg: 'bg-gray-100 dark:bg-gray-800',
      buttonText: 'text-gray-600 dark:text-gray-300',
      accentBg: 'bg-gray-900 dark:bg-white',
      accentText: 'text-white dark:text-gray-900',
    },
  },

  // 4. MODERN GLASS - Glassmorphism with blur effects
  glass: {
    id: 'glass',
    name: 'Glass',
    description: 'Modern glassmorphism with translucent surfaces',
    board: {
      background: 'bg-gradient-to-br from-indigo-100/50 via-purple-100/30 to-pink-100/50 dark:from-indigo-950 dark:via-purple-950 dark:to-pink-950',
      columnGap: 'gap-4',
      padding: 'p-5',
      borderRadius: 'rounded-2xl',
    },
    column: {
      background: 'bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl',
      headerBackground: 'bg-white/40 dark:bg-gray-800/40 backdrop-blur-lg',
      borderRadius: 'rounded-2xl',
      border: 'border border-white/50 dark:border-gray-700/50',
      shadow: 'shadow-xl shadow-black/5',
      headerTextColor: 'text-gray-900 dark:text-white',
      subtitleTextColor: 'text-gray-600 dark:text-gray-400',
      countBadge: {
        background: 'bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm',
        textColor: 'text-gray-700 dark:text-gray-300',
        borderRadius: 'rounded-full',
      },
      colorIntensity: 'medium',
    },
    card: {
      background: 'bg-white/80 dark:bg-gray-800/80 backdrop-blur-md',
      backgroundHover: 'hover:bg-white/95 dark:hover:bg-gray-800/95',
      border: 'border border-white/60 dark:border-gray-700/60',
      borderRadius: 'rounded-xl',
      shadow: 'shadow-lg shadow-black/5',
      shadowHover: 'hover:shadow-xl',
      padding: 'p-3.5',
      priorityBorderWidth: 'border-l-[3px]',
      titleSize: 'text-sm',
      titleWeight: 'font-medium',
      titleColor: 'text-gray-900 dark:text-gray-100',
      metaSize: 'text-xs',
      metaColor: 'text-gray-600 dark:text-gray-300',
      itemNumberBg: 'bg-white/60 dark:bg-gray-700/60',
      itemNumberColor: 'text-gray-600 dark:text-gray-300',
      tagStyle: 'pill',
    },
    dragDrop: {
      dragCardScale: 'scale-105',
      dragCardRotate: 'rotate-1',
      dragCardShadow: 'shadow-2xl shadow-purple-500/20',
      dropZoneBackground: 'bg-purple-200/30 dark:bg-purple-900/20 backdrop-blur-md',
      dropZoneBorder: 'ring-2 ring-purple-400/60',
      dropZoneScale: 'scale-[1.02]',
    },
    animations: {
      cardHover: true,
      columnExpand: true,
      smoothScroll: true,
      dragGhost: true,
    },
    views: {
      containerBg: 'bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl',
      headerBg: 'bg-white/40 dark:bg-gray-800/40 backdrop-blur-lg',
      containerBorder: 'border-white/50 dark:border-gray-700/50',
      dividerBorder: 'border-white/30 dark:border-gray-700/30',
      headingText: 'text-gray-900 dark:text-white',
      bodyText: 'text-gray-700 dark:text-gray-300',
      mutedText: 'text-gray-600 dark:text-gray-300',
      rowBg: 'bg-white/50 dark:bg-gray-800/50',
      rowHoverBg: 'hover:bg-white/70 dark:hover:bg-gray-800/70',
      rowBorderColor: 'divide-white/20 dark:divide-gray-700/30',
      selectedRowBg: 'bg-purple-100/50 dark:bg-purple-900/20',
      inputBg: 'bg-white/50 dark:bg-gray-800/50',
      inputBorder: 'border-white/40 dark:border-gray-700/40',
      buttonBg: 'bg-white/40 dark:bg-gray-700/40',
      buttonText: 'text-gray-700 dark:text-gray-300',
      accentBg: 'bg-purple-600',
      accentText: 'text-white',
    },
  },

  // 5. DARK PRO - Professional dark theme (always dark regardless of system mode)
  darkPro: {
    id: 'darkPro',
    name: 'Dark Pro',
    description: 'Professional dark theme for power users',
    board: {
      background: 'bg-gray-950',
      columnGap: 'gap-3',
      padding: 'p-4',
      borderRadius: 'rounded-lg',
    },
    column: {
      background: 'bg-gray-900',
      headerBackground: 'bg-gray-900',
      borderRadius: 'rounded-lg',
      border: 'border border-gray-800',
      shadow: 'shadow-none',
      headerTextColor: 'text-gray-100',
      subtitleTextColor: 'text-gray-300',
      countBadge: {
        background: 'bg-gray-800',
        textColor: 'text-gray-300',
        borderRadius: 'rounded-md',
      },
      colorIntensity: 'subtle',
    },
    card: {
      background: 'bg-gray-900',
      backgroundHover: 'hover:bg-gray-800',
      border: 'border border-gray-600/60',
      borderRadius: 'rounded-md',
      shadow: 'shadow-none',
      shadowHover: 'hover:shadow-md hover:shadow-black/50',
      padding: 'p-2.5',
      priorityBorderWidth: 'border-l-2',
      titleSize: 'text-sm',
      titleWeight: 'font-medium',
      titleColor: 'text-gray-100',
      metaSize: 'text-xs',
      metaColor: 'text-gray-300',
      itemNumberBg: 'bg-gray-800',
      itemNumberColor: 'text-gray-300',
      tagStyle: 'square',
    },
    dragDrop: {
      dragCardScale: 'scale-105',
      dragCardRotate: 'rotate-1',
      dragCardShadow: 'shadow-xl shadow-black/50',
      dropZoneBackground: 'bg-blue-950/50',
      dropZoneBorder: 'ring-1 ring-blue-500',
      dropZoneScale: 'scale-[1.01]',
    },
    animations: {
      cardHover: true,
      columnExpand: false,
      smoothScroll: true,
      dragGhost: true,
    },
    views: {
      containerBg: 'bg-gray-900',
      headerBg: 'bg-gray-900',
      containerBorder: 'border-gray-700',
      dividerBorder: 'border-gray-800',
      headingText: 'text-gray-100',
      bodyText: 'text-gray-300',
      mutedText: 'text-gray-300',
      rowBg: 'bg-gray-900',
      rowHoverBg: 'hover:bg-gray-800',
      rowBorderColor: 'divide-gray-800',
      selectedRowBg: 'bg-blue-900/30',
      inputBg: 'bg-gray-800',
      inputBorder: 'border-gray-700',
      buttonBg: 'bg-gray-800',
      buttonText: 'text-gray-300',
      accentBg: 'bg-blue-600',
      accentText: 'text-white',
    },
  },

  // 6. NEON - Cyberpunk-inspired with glowing accents (always dark)
  neon: {
    id: 'neon',
    name: 'Neon',
    description: 'Cyberpunk-inspired with glowing accent colors',
    board: {
      background: 'bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950',
      columnGap: 'gap-4',
      padding: 'p-5',
      borderRadius: 'rounded-xl',
    },
    column: {
      background: 'bg-gray-900/80 backdrop-blur-md',
      headerBackground: 'bg-gradient-to-r',
      borderRadius: 'rounded-xl',
      border: 'border border-gray-800',
      shadow: 'shadow-lg',
      headerTextColor: 'text-white',
      subtitleTextColor: 'text-white/70',
      countBadge: {
        background: 'bg-black/50',
        textColor: 'text-white',
        borderRadius: 'rounded-md',
      },
      colorIntensity: 'vibrant',
    },
    card: {
      background: 'bg-gray-900',
      backgroundHover: 'hover:bg-gray-800',
      border: 'border border-gray-600/60 hover:border-purple-500/50',
      borderRadius: 'rounded-lg',
      shadow: 'shadow-md shadow-purple-500/10',
      shadowHover: 'hover:shadow-lg hover:shadow-purple-500/20',
      padding: 'p-3',
      priorityBorderWidth: 'border-l-[3px]',
      titleSize: 'text-sm',
      titleWeight: 'font-medium',
      titleColor: 'text-gray-100',
      metaSize: 'text-xs',
      metaColor: 'text-gray-300',
      itemNumberBg: 'bg-gray-800',
      itemNumberColor: 'text-gray-300',
      tagStyle: 'outline',
    },
    dragDrop: {
      dragCardScale: 'scale-110',
      dragCardRotate: 'rotate-2',
      dragCardShadow: 'shadow-2xl shadow-cyan-500/30',
      dropZoneBackground: 'bg-purple-950/50',
      dropZoneBorder: 'ring-2 ring-purple-500 animate-pulse',
      dropZoneScale: 'scale-[1.02]',
    },
    animations: {
      cardHover: true,
      columnExpand: true,
      smoothScroll: true,
      dragGhost: true,
    },
    views: {
      containerBg: 'bg-gray-950',
      headerBg: 'bg-gray-900',
      containerBorder: 'border-gray-800',
      dividerBorder: 'border-gray-800',
      headingText: 'text-gray-100',
      bodyText: 'text-gray-300',
      mutedText: 'text-gray-300',
      rowBg: 'bg-gray-950',
      rowHoverBg: 'hover:bg-gray-900',
      rowBorderColor: 'divide-gray-800',
      selectedRowBg: 'bg-purple-900/30',
      inputBg: 'bg-gray-900',
      inputBorder: 'border-gray-800',
      buttonBg: 'bg-gray-800',
      buttonText: 'text-gray-300',
      accentBg: 'bg-purple-600',
      accentText: 'text-white',
    },
  },

  // 7. PASTEL - Soft pastel colors, calm and gentle
  pastel: {
    id: 'pastel',
    name: 'Pastel',
    description: 'Soft, calming pastel color palette',
    board: {
      background: 'bg-gradient-to-br from-rose-50 via-sky-50 to-violet-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900',
      columnGap: 'gap-5',
      padding: 'p-5',
      borderRadius: 'rounded-2xl',
    },
    column: {
      background: 'bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm',
      headerBackground: 'bg-gradient-to-br from-white/80 to-gray-50/80 dark:from-gray-800/80 dark:to-gray-900/80',
      borderRadius: 'rounded-2xl',
      border: 'border border-gray-200/50 dark:border-gray-700/50',
      shadow: 'shadow-sm',
      headerTextColor: 'text-gray-700 dark:text-gray-200',
      subtitleTextColor: 'text-gray-500 dark:text-gray-300',
      countBadge: {
        background: 'bg-white/80 dark:bg-gray-700/80',
        textColor: 'text-gray-600 dark:text-gray-300',
        borderRadius: 'rounded-full',
      },
      colorIntensity: 'subtle',
    },
    card: {
      background: 'bg-white dark:bg-gray-800',
      backgroundHover: 'hover:bg-gray-50 dark:hover:bg-gray-700',
      border: 'border border-gray-100 dark:border-gray-700',
      borderRadius: 'rounded-xl',
      shadow: 'shadow-sm',
      shadowHover: 'hover:shadow-md',
      padding: 'p-3.5',
      priorityBorderWidth: 'border-l-[3px]',
      titleSize: 'text-sm',
      titleWeight: 'font-medium',
      titleColor: 'text-gray-800 dark:text-gray-100',
      metaSize: 'text-xs',
      metaColor: 'text-gray-500 dark:text-gray-300',
      itemNumberBg: 'bg-gray-100 dark:bg-gray-700',
      itemNumberColor: 'text-gray-500 dark:text-gray-300',
      tagStyle: 'pill',
    },
    dragDrop: {
      dragCardScale: 'scale-105',
      dragCardRotate: 'rotate-1',
      dragCardShadow: 'shadow-xl shadow-rose-500/10',
      dropZoneBackground: 'bg-rose-100/50 dark:bg-rose-900/20',
      dropZoneBorder: 'ring-2 ring-rose-300',
      dropZoneScale: 'scale-[1.02]',
    },
    animations: {
      cardHover: true,
      columnExpand: true,
      smoothScroll: true,
      dragGhost: true,
    },
    views: {
      containerBg: 'bg-white dark:bg-gray-800',
      headerBg: 'bg-gradient-to-br from-rose-50/50 to-sky-50/50 dark:from-gray-900 dark:to-gray-900',
      containerBorder: 'border-gray-200/50 dark:border-gray-700/50',
      dividerBorder: 'border-gray-200/50 dark:border-gray-700/50',
      headingText: 'text-gray-700 dark:text-gray-200',
      bodyText: 'text-gray-600 dark:text-gray-300',
      mutedText: 'text-gray-500 dark:text-gray-300',
      rowBg: 'bg-white dark:bg-gray-800',
      rowHoverBg: 'hover:bg-rose-50/30 dark:hover:bg-gray-700',
      rowBorderColor: 'divide-gray-100 dark:divide-gray-700',
      selectedRowBg: 'bg-rose-50 dark:bg-rose-900/20',
      inputBg: 'bg-white dark:bg-gray-800',
      inputBorder: 'border-gray-200 dark:border-gray-700',
      buttonBg: 'bg-gray-100 dark:bg-gray-700',
      buttonText: 'text-gray-600 dark:text-gray-300',
      accentBg: 'bg-rose-500',
      accentText: 'text-white',
    },
  },

  // 8. CORPORATE - Enterprise, serious business look
  corporate: {
    id: 'corporate',
    name: 'Corporate',
    description: 'Professional corporate appearance',
    board: {
      background: 'bg-slate-100 dark:bg-slate-900',
      columnGap: 'gap-4',
      padding: 'p-4',
      borderRadius: 'rounded-lg',
    },
    column: {
      background: 'bg-white dark:bg-slate-800',
      headerBackground: 'bg-slate-50 dark:bg-slate-900',
      borderRadius: 'rounded-lg',
      border: 'border border-slate-200 dark:border-slate-700',
      shadow: 'shadow-sm',
      headerTextColor: 'text-slate-900 dark:text-slate-100',
      subtitleTextColor: 'text-slate-500 dark:text-slate-400',
      countBadge: {
        background: 'bg-slate-200 dark:bg-slate-700',
        textColor: 'text-slate-700 dark:text-slate-300',
        borderRadius: 'rounded-md',
      },
      colorIntensity: 'medium',
    },
    card: {
      background: 'bg-white dark:bg-slate-800',
      backgroundHover: 'hover:bg-slate-50 dark:hover:bg-slate-700',
      border: 'border border-slate-200 dark:border-slate-700',
      borderRadius: 'rounded-md',
      shadow: 'shadow-sm',
      shadowHover: 'hover:shadow',
      padding: 'p-3',
      priorityBorderWidth: 'border-l-[3px]',
      titleSize: 'text-sm',
      titleWeight: 'font-medium',
      titleColor: 'text-slate-900 dark:text-slate-100',
      metaSize: 'text-xs',
      metaColor: 'text-slate-500 dark:text-slate-400',
      itemNumberBg: 'bg-slate-100 dark:bg-slate-700',
      itemNumberColor: 'text-slate-500 dark:text-slate-400',
      tagStyle: 'square',
    },
    dragDrop: {
      dragCardScale: 'scale-102',
      dragCardRotate: 'rotate-0',
      dragCardShadow: 'shadow-lg',
      dropZoneBackground: 'bg-blue-50 dark:bg-blue-950/30',
      dropZoneBorder: 'ring-2 ring-blue-500',
      dropZoneScale: 'scale-[1.01]',
    },
    animations: {
      cardHover: true,
      columnExpand: false,
      smoothScroll: true,
      dragGhost: false,
    },
    views: {
      containerBg: 'bg-white dark:bg-slate-800',
      headerBg: 'bg-slate-50 dark:bg-slate-900',
      containerBorder: 'border-slate-200 dark:border-slate-700',
      dividerBorder: 'border-slate-200 dark:border-slate-700',
      headingText: 'text-slate-900 dark:text-slate-100',
      bodyText: 'text-slate-700 dark:text-slate-300',
      mutedText: 'text-slate-500 dark:text-slate-400',
      rowBg: 'bg-white dark:bg-slate-800',
      rowHoverBg: 'hover:bg-slate-50 dark:hover:bg-slate-700',
      rowBorderColor: 'divide-slate-100 dark:divide-slate-700',
      selectedRowBg: 'bg-blue-50 dark:bg-blue-900/20',
      inputBg: 'bg-white dark:bg-slate-800',
      inputBorder: 'border-slate-200 dark:border-slate-700',
      buttonBg: 'bg-slate-100 dark:bg-slate-700',
      buttonText: 'text-slate-700 dark:text-slate-300',
      accentBg: 'bg-blue-600',
      accentText: 'text-white',
    },
  },

  // 9. COMPACT JIRA - Jira-style dense layout
  jira: {
    id: 'jira',
    name: 'Jira Style',
    description: 'Compact, information-dense like Jira',
    board: {
      background: 'bg-gray-100 dark:bg-gray-900',
      columnGap: 'gap-2',
      padding: 'p-2',
      borderRadius: 'rounded-md',
    },
    column: {
      background: 'bg-gray-200/50 dark:bg-gray-800/50',
      headerBackground: 'bg-transparent',
      borderRadius: 'rounded-md',
      border: 'border-0',
      shadow: 'shadow-none',
      headerTextColor: 'text-gray-700 dark:text-gray-300',
      subtitleTextColor: 'text-gray-500 dark:text-gray-300',
      countBadge: {
        background: 'bg-gray-300 dark:bg-gray-700',
        textColor: 'text-gray-600 dark:text-gray-400',
        borderRadius: 'rounded-full',
      },
      colorIntensity: 'subtle',
    },
    card: {
      background: 'bg-white dark:bg-gray-800',
      backgroundHover: 'hover:bg-blue-50 dark:hover:bg-gray-700',
      border: 'border border-gray-200 dark:border-gray-700',
      borderRadius: 'rounded-sm',
      shadow: 'shadow-sm',
      shadowHover: 'hover:shadow-md',
      padding: 'p-2',
      priorityBorderWidth: 'border-l-2',
      titleSize: 'text-xs',
      titleWeight: 'font-medium',
      titleColor: 'text-gray-900 dark:text-gray-100',
      metaSize: 'text-[10px]',
      metaColor: 'text-gray-500 dark:text-gray-400',
      itemNumberBg: 'bg-gray-100 dark:bg-gray-700',
      itemNumberColor: 'text-gray-500 dark:text-gray-300',
      tagStyle: 'minimal',
    },
    dragDrop: {
      dragCardScale: 'scale-100',
      dragCardRotate: 'rotate-0',
      dragCardShadow: 'shadow-xl',
      dropZoneBackground: 'bg-blue-100 dark:bg-blue-900/30',
      dropZoneBorder: 'ring-2 ring-blue-400',
      dropZoneScale: 'scale-100',
    },
    animations: {
      cardHover: true,
      columnExpand: false,
      smoothScroll: true,
      dragGhost: false,
    },
    views: {
      containerBg: 'bg-white dark:bg-gray-800',
      headerBg: 'bg-gray-50 dark:bg-gray-900',
      containerBorder: 'border-gray-200 dark:border-gray-700',
      dividerBorder: 'border-gray-200 dark:border-gray-700',
      headingText: 'text-gray-700 dark:text-gray-300',
      bodyText: 'text-gray-700 dark:text-gray-300',
      mutedText: 'text-gray-500 dark:text-gray-300',
      rowBg: 'bg-white dark:bg-gray-800',
      rowHoverBg: 'hover:bg-blue-50 dark:hover:bg-gray-700',
      rowBorderColor: 'divide-gray-100 dark:divide-gray-800',
      selectedRowBg: 'bg-blue-50 dark:bg-blue-900/20',
      inputBg: 'bg-white dark:bg-gray-800',
      inputBorder: 'border-gray-200 dark:border-gray-600',
      buttonBg: 'bg-gray-100 dark:bg-gray-700',
      buttonText: 'text-gray-600 dark:text-gray-300',
      accentBg: 'bg-blue-500',
      accentText: 'text-white',
    },
  },

  // 10. GRADIENT CARDS - Cards with gradient backgrounds based on status
  gradientCards: {
    id: 'gradientCards',
    name: 'Gradient Cards',
    description: 'Cards with beautiful gradient backgrounds',
    board: {
      background: 'bg-gray-100 dark:bg-gray-900',
      columnGap: 'gap-4',
      padding: 'p-4',
      borderRadius: 'rounded-xl',
    },
    column: {
      background: 'bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm',
      headerBackground: 'bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900',
      borderRadius: 'rounded-xl',
      border: 'border border-gray-200/50 dark:border-gray-700/50',
      shadow: 'shadow-sm',
      headerTextColor: 'text-gray-900 dark:text-white',
      subtitleTextColor: 'text-gray-500 dark:text-gray-300',
      countBadge: {
        background: 'bg-white dark:bg-gray-700',
        textColor: 'text-gray-700 dark:text-gray-300',
        borderRadius: 'rounded-full',
      },
      colorIntensity: 'medium',
    },
    card: {
      background: 'bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900',
      backgroundHover: 'hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-700 dark:hover:via-gray-700 dark:hover:to-gray-800',
      border: 'border border-gray-200/70 dark:border-gray-700/70',
      borderRadius: 'rounded-xl',
      shadow: 'shadow-md shadow-black/5',
      shadowHover: 'hover:shadow-lg hover:shadow-black/10',
      padding: 'p-3.5',
      priorityBorderWidth: 'border-l-4',
      titleSize: 'text-sm',
      titleWeight: 'font-semibold',
      titleColor: 'text-gray-900 dark:text-gray-100',
      metaSize: 'text-xs',
      metaColor: 'text-gray-600 dark:text-gray-300',
      itemNumberBg: 'bg-gray-100 dark:bg-gray-700',
      itemNumberColor: 'text-gray-500 dark:text-gray-300',
      tagStyle: 'pill',
    },
    dragDrop: {
      dragCardScale: 'scale-105',
      dragCardRotate: 'rotate-2',
      dragCardShadow: 'shadow-2xl shadow-black/20',
      dropZoneBackground: 'bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30',
      dropZoneBorder: 'ring-2 ring-blue-400',
      dropZoneScale: 'scale-[1.02]',
    },
    animations: {
      cardHover: true,
      columnExpand: true,
      smoothScroll: true,
      dragGhost: true,
    },
    views: {
      containerBg: 'bg-white dark:bg-gray-800',
      headerBg: 'bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900',
      containerBorder: 'border-gray-200/50 dark:border-gray-700/50',
      dividerBorder: 'border-gray-200/50 dark:border-gray-700/50',
      headingText: 'text-gray-900 dark:text-white',
      bodyText: 'text-gray-700 dark:text-gray-300',
      mutedText: 'text-gray-600 dark:text-gray-300',
      rowBg: 'bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900',
      rowHoverBg: 'hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-700 dark:hover:to-gray-800',
      rowBorderColor: 'divide-gray-100 dark:divide-gray-700',
      selectedRowBg: 'bg-blue-50 dark:bg-blue-900/20',
      inputBg: 'bg-white dark:bg-gray-800',
      inputBorder: 'border-gray-200 dark:border-gray-700',
      buttonBg: 'bg-gray-100 dark:bg-gray-700',
      buttonText: 'text-gray-700 dark:text-gray-300',
      accentBg: 'bg-blue-600',
      accentText: 'text-white',
    },
  },
};

// ============================================================================
// DEFAULT PRESET
// ============================================================================

export const DEFAULT_KANBAN_STYLE = 'classic';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get a style preset by ID, falling back to classic if not found
 */
export function getKanbanStylePreset(presetId?: string): KanbanStylePreset {
  if (!presetId) return KANBAN_STYLE_PRESETS.classic;
  return KANBAN_STYLE_PRESETS[presetId] || KANBAN_STYLE_PRESETS.classic;
}

/**
 * Get all available presets as an array for selection UI
 */
export function getAllKanbanStylePresets(): KanbanStylePreset[] {
  return Object.values(KANBAN_STYLE_PRESETS);
}

/**
 * Get vibrant column header colors based on preset and base color
 */
export function getColumnHeaderColors(
  presetId: string,
  baseColor: string
): { background: string; text: string; border: string; accent: string } {
  const preset = getKanbanStylePreset(presetId);
  const intensity = preset.column.colorIntensity;
  
  // Define color mappings with different intensity levels
  const colorMappings: Record<string, Record<'subtle' | 'medium' | 'vibrant', {
    background: string;
    text: string;
    border: string;
    accent: string;
  }>> = {
    blue: {
      subtle: {
        background: 'from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/30',
        text: 'text-blue-700 dark:text-blue-300',
        border: 'border-blue-200 dark:border-blue-800',
        accent: 'bg-blue-100 dark:bg-blue-900/50',
      },
      medium: {
        background: 'from-blue-100 to-blue-200 dark:from-blue-900/60 dark:to-blue-800/40',
        text: 'text-blue-800 dark:text-blue-200',
        border: 'border-blue-300 dark:border-blue-700',
        accent: 'bg-blue-200 dark:bg-blue-800/60',
      },
      vibrant: {
        background: 'from-blue-500 via-blue-600 to-indigo-600',
        text: 'text-white',
        border: 'border-blue-600 dark:border-blue-500',
        accent: 'bg-blue-600/20',
      },
    },
    green: {
      subtle: {
        background: 'from-emerald-50 to-emerald-100 dark:from-emerald-950/50 dark:to-emerald-900/30',
        text: 'text-emerald-700 dark:text-emerald-300',
        border: 'border-emerald-200 dark:border-emerald-800',
        accent: 'bg-emerald-100 dark:bg-emerald-900/50',
      },
      medium: {
        background: 'from-emerald-100 to-emerald-200 dark:from-emerald-900/60 dark:to-emerald-800/40',
        text: 'text-emerald-800 dark:text-emerald-200',
        border: 'border-emerald-300 dark:border-emerald-700',
        accent: 'bg-emerald-200 dark:bg-emerald-800/60',
      },
      vibrant: {
        background: 'from-emerald-500 via-emerald-600 to-teal-600',
        text: 'text-white',
        border: 'border-emerald-600 dark:border-emerald-500',
        accent: 'bg-emerald-600/20',
      },
    },
    yellow: {
      subtle: {
        background: 'from-yellow-50 to-amber-100 dark:from-yellow-950/50 dark:to-amber-900/30',
        text: 'text-yellow-700 dark:text-yellow-300',
        border: 'border-yellow-200 dark:border-yellow-800',
        accent: 'bg-yellow-100 dark:bg-yellow-900/50',
      },
      medium: {
        background: 'from-yellow-100 to-amber-200 dark:from-yellow-900/60 dark:to-amber-800/40',
        text: 'text-yellow-800 dark:text-amber-200',
        border: 'border-yellow-300 dark:border-yellow-700',
        accent: 'bg-yellow-200 dark:bg-yellow-800/60',
      },
      vibrant: {
        background: 'from-yellow-400 via-amber-500 to-orange-500',
        text: 'text-white',
        border: 'border-amber-500 dark:border-amber-400',
        accent: 'bg-amber-500/20',
      },
    },
    orange: {
      subtle: {
        background: 'from-orange-50 to-orange-100 dark:from-orange-950/50 dark:to-orange-900/30',
        text: 'text-orange-700 dark:text-orange-300',
        border: 'border-orange-200 dark:border-orange-800',
        accent: 'bg-orange-100 dark:bg-orange-900/50',
      },
      medium: {
        background: 'from-orange-100 to-orange-200 dark:from-orange-900/60 dark:to-orange-800/40',
        text: 'text-orange-800 dark:text-orange-200',
        border: 'border-orange-300 dark:border-orange-700',
        accent: 'bg-orange-200 dark:bg-orange-800/60',
      },
      vibrant: {
        background: 'from-orange-500 via-orange-600 to-red-500',
        text: 'text-white',
        border: 'border-orange-600 dark:border-orange-500',
        accent: 'bg-orange-600/20',
      },
    },
    red: {
      subtle: {
        background: 'from-red-50 to-rose-100 dark:from-red-950/50 dark:to-rose-900/30',
        text: 'text-red-700 dark:text-red-300',
        border: 'border-red-200 dark:border-red-800',
        accent: 'bg-red-100 dark:bg-red-900/50',
      },
      medium: {
        background: 'from-red-100 to-rose-200 dark:from-red-900/60 dark:to-rose-800/40',
        text: 'text-red-800 dark:text-red-200',
        border: 'border-red-300 dark:border-red-700',
        accent: 'bg-red-200 dark:bg-red-800/60',
      },
      vibrant: {
        background: 'from-red-500 via-red-600 to-rose-600',
        text: 'text-white',
        border: 'border-red-600 dark:border-red-500',
        accent: 'bg-red-600/20',
      },
    },
    purple: {
      subtle: {
        background: 'from-purple-50 to-violet-100 dark:from-purple-950/50 dark:to-violet-900/30',
        text: 'text-purple-700 dark:text-purple-300',
        border: 'border-purple-200 dark:border-purple-800',
        accent: 'bg-purple-100 dark:bg-purple-900/50',
      },
      medium: {
        background: 'from-purple-100 to-violet-200 dark:from-purple-900/60 dark:to-violet-800/40',
        text: 'text-purple-800 dark:text-purple-200',
        border: 'border-purple-300 dark:border-purple-700',
        accent: 'bg-purple-200 dark:bg-purple-800/60',
      },
      vibrant: {
        background: 'from-purple-500 via-purple-600 to-fuchsia-600',
        text: 'text-white',
        border: 'border-purple-600 dark:border-purple-500',
        accent: 'bg-purple-600/20',
      },
    },
    cyan: {
      subtle: {
        background: 'from-cyan-50 to-teal-100 dark:from-cyan-950/50 dark:to-teal-900/30',
        text: 'text-cyan-700 dark:text-cyan-300',
        border: 'border-cyan-200 dark:border-cyan-800',
        accent: 'bg-cyan-100 dark:bg-cyan-900/50',
      },
      medium: {
        background: 'from-cyan-100 to-teal-200 dark:from-cyan-900/60 dark:to-teal-800/40',
        text: 'text-cyan-800 dark:text-cyan-200',
        border: 'border-cyan-300 dark:border-cyan-700',
        accent: 'bg-cyan-200 dark:bg-cyan-800/60',
      },
      vibrant: {
        background: 'from-cyan-500 via-cyan-600 to-teal-600',
        text: 'text-white',
        border: 'border-cyan-600 dark:border-cyan-500',
        accent: 'bg-cyan-600/20',
      },
    },
    gray: {
      subtle: {
        background: 'from-gray-50 to-slate-100 dark:from-gray-900/50 dark:to-slate-800/30',
        text: 'text-gray-700 dark:text-gray-300',
        border: 'border-gray-200 dark:border-gray-700',
        accent: 'bg-gray-100 dark:bg-gray-800/50',
      },
      medium: {
        background: 'from-gray-100 to-slate-200 dark:from-gray-800/60 dark:to-slate-700/40',
        text: 'text-gray-800 dark:text-gray-200',
        border: 'border-gray-300 dark:border-gray-600',
        accent: 'bg-gray-200 dark:bg-gray-700/60',
      },
      vibrant: {
        background: 'from-slate-500 via-slate-600 to-gray-700',
        text: 'text-white',
        border: 'border-slate-600 dark:border-slate-500',
        accent: 'bg-slate-600/20',
      },
    },
    amber: {
      subtle: {
        background: 'from-amber-50 to-yellow-100 dark:from-amber-950/50 dark:to-yellow-900/30',
        text: 'text-amber-700 dark:text-amber-300',
        border: 'border-amber-200 dark:border-amber-800',
        accent: 'bg-amber-100 dark:bg-amber-900/50',
      },
      medium: {
        background: 'from-amber-100 to-yellow-200 dark:from-amber-900/60 dark:to-yellow-800/40',
        text: 'text-amber-800 dark:text-amber-200',
        border: 'border-amber-300 dark:border-amber-700',
        accent: 'bg-amber-200 dark:bg-amber-800/60',
      },
      vibrant: {
        background: 'from-amber-500 via-amber-600 to-orange-600',
        text: 'text-white',
        border: 'border-amber-600 dark:border-amber-500',
        accent: 'bg-amber-600/20',
      },
    },
    teal: {
      subtle: {
        background: 'from-teal-50 to-cyan-100 dark:from-teal-950/50 dark:to-cyan-900/30',
        text: 'text-teal-700 dark:text-teal-300',
        border: 'border-teal-200 dark:border-teal-800',
        accent: 'bg-teal-100 dark:bg-teal-900/50',
      },
      medium: {
        background: 'from-teal-100 to-cyan-200 dark:from-teal-900/60 dark:to-cyan-800/40',
        text: 'text-teal-800 dark:text-teal-200',
        border: 'border-teal-300 dark:border-teal-700',
        accent: 'bg-teal-200 dark:bg-teal-800/60',
      },
      vibrant: {
        background: 'from-teal-500 via-teal-600 to-emerald-600',
        text: 'text-white',
        border: 'border-teal-600 dark:border-teal-500',
        accent: 'bg-teal-600/20',
      },
    },
    indigo: {
      subtle: {
        background: 'from-indigo-50 to-blue-100 dark:from-indigo-950/50 dark:to-blue-900/30',
        text: 'text-indigo-700 dark:text-indigo-300',
        border: 'border-indigo-200 dark:border-indigo-800',
        accent: 'bg-indigo-100 dark:bg-indigo-900/50',
      },
      medium: {
        background: 'from-indigo-100 to-blue-200 dark:from-indigo-900/60 dark:to-blue-800/40',
        text: 'text-indigo-800 dark:text-indigo-200',
        border: 'border-indigo-300 dark:border-indigo-700',
        accent: 'bg-indigo-200 dark:bg-indigo-800/60',
      },
      vibrant: {
        background: 'from-indigo-500 via-indigo-600 to-violet-600',
        text: 'text-white',
        border: 'border-indigo-600 dark:border-indigo-500',
        accent: 'bg-indigo-600/20',
      },
    },
    emerald: {
      subtle: {
        background: 'from-emerald-50 to-green-100 dark:from-emerald-950/50 dark:to-green-900/30',
        text: 'text-emerald-700 dark:text-emerald-300',
        border: 'border-emerald-200 dark:border-emerald-800',
        accent: 'bg-emerald-100 dark:bg-emerald-900/50',
      },
      medium: {
        background: 'from-emerald-100 to-green-200 dark:from-emerald-900/60 dark:to-green-800/40',
        text: 'text-emerald-800 dark:text-emerald-200',
        border: 'border-emerald-300 dark:border-emerald-700',
        accent: 'bg-emerald-200 dark:bg-emerald-800/60',
      },
      vibrant: {
        background: 'from-emerald-500 via-emerald-600 to-teal-600',
        text: 'text-white',
        border: 'border-emerald-600 dark:border-emerald-500',
        accent: 'bg-emerald-600/20',
      },
    },
    violet: {
      subtle: {
        background: 'from-violet-50 to-purple-100 dark:from-violet-950/50 dark:to-purple-900/30',
        text: 'text-violet-700 dark:text-violet-300',
        border: 'border-violet-200 dark:border-violet-800',
        accent: 'bg-violet-100 dark:bg-violet-900/50',
      },
      medium: {
        background: 'from-violet-100 to-purple-200 dark:from-violet-900/60 dark:to-purple-800/40',
        text: 'text-violet-800 dark:text-violet-200',
        border: 'border-violet-300 dark:border-violet-700',
        accent: 'bg-violet-200 dark:bg-violet-800/60',
      },
      vibrant: {
        background: 'from-violet-500 via-violet-600 to-purple-600',
        text: 'text-white',
        border: 'border-violet-600 dark:border-violet-500',
        accent: 'bg-violet-600/20',
      },
    },
    slate: {
      subtle: {
        background: 'from-slate-50 to-gray-100 dark:from-slate-900/50 dark:to-gray-800/30',
        text: 'text-slate-700 dark:text-slate-300',
        border: 'border-slate-200 dark:border-slate-700',
        accent: 'bg-slate-100 dark:bg-slate-800/50',
      },
      medium: {
        background: 'from-slate-100 to-gray-200 dark:from-slate-800/60 dark:to-gray-700/40',
        text: 'text-slate-800 dark:text-slate-200',
        border: 'border-slate-300 dark:border-slate-600',
        accent: 'bg-slate-200 dark:bg-slate-700/60',
      },
      vibrant: {
        background: 'from-slate-500 via-slate-600 to-gray-700',
        text: 'text-white',
        border: 'border-slate-600 dark:border-slate-500',
        accent: 'bg-slate-600/20',
      },
    },
  };
  
  // Fallback to gray if color not found
  const colorConfig = colorMappings[baseColor] || colorMappings.gray;
  return colorConfig[intensity];
}

/**
 * Apply card styles based on preset and priority
 */
export function getCardStyles(presetId: string, priority?: string): string {
  const preset = getKanbanStylePreset(presetId);
  const { card } = preset;
  
  const priorityBorderColors: Record<string, string> = {
    urgent: 'border-l-red-600',
    critical: 'border-l-red-700',
    high: 'border-l-orange-500',
    medium: 'border-l-yellow-500',
    low: 'border-l-green-500',
  };
  
  const priorityBorder = priorityBorderColors[priority || 'medium'] || 'border-l-gray-400';
  
  return `
    ${card.background} ${card.backgroundHover} 
    ${card.border} ${card.borderRadius} 
    ${card.shadow} ${card.shadowHover}
    ${card.padding} ${card.priorityBorderWidth} ${priorityBorder}
  `.trim();
}
